import { useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CalendarDays, FileText, Lock, MapPin, Users, Wallet } from 'lucide-react';
import {
  Modal, Form, Input, Select, InputNumber, Button, Tag, Alert, message, cn,
  SectionHead, SECTION_TINT,
} from '../../ui';
import { useConfirmedMutation } from '../../lib/confirm';
import { useHasRole } from '../../store/auth';
import { request } from '../../api/client';
import { CopyValue } from '../../components/CopyValue';
import { TERRITORY_LEVEL } from '../../components/TerritoryChips';
import { EVENT_TYPES, TYPE_META, STATUS_TONE, statusLabel } from './eventMeta';
import { inr } from '../../lib/format';
import { formatIst } from '../../lib/time';
import { MeetWhenField } from './MeetWhenField';

/**
 * What KIND of meet this is — each with its own glyph and hue, in the dropdown AND on the chosen
 * value. Eight lines of identical grey text is a list you read one at a time; a mark you recognise
 * is one you pick from.
 *
 * The pairs come from `eventMeta`, which the list, the Type filter and the detail header read too —
 * this form had its own set, so a retailer meet was sky here and grey in the register, and picking
 * "Product launch" gave you a gold mark that the page then drew in another colour entirely.
 */
const TYPES = EVENT_TYPES.map((value) => {
  const m = TYPE_META[value];
  return { value, label: m.label, icon: <m.Icon size={15} />, chip: m.tint };
});
/**
 * The same mark on the CLOSED control, not only in the open list. A picker whose options are
 * colour-coded and whose chosen value is plain grey text makes you open it again to check what you
 * chose. `selectedText` is what ui/Select renders in the trigger (`optionLabelProp`).
 */
const TYPE_OPTIONS = TYPES.map((t) => ({
  ...t,
  selectedText: (
    <span className="flex items-center gap-2">
      <span className={cn('grid size-5 shrink-0 place-items-center rounded', t.chip)}>{t.icon}</span>
      {t.label}
    </span>
  ),
}));
const TYPE_BY_VALUE = new Map(TYPES.map((t) => [t.value, t]));

/** Cadence is a reporting label, not a scheduler — so each option says what it does, and does not do. */
const CADENCES = [
  { value: 'ONE_TIME', label: 'One-time', note: 'Held once' },
  { value: 'MONTHLY', label: 'Monthly', note: 'Part of a monthly series' },
  { value: 'QUARTERLY', label: 'Quarterly', note: 'Part of a quarterly series' },
  { value: 'YEARLY', label: 'Yearly', note: 'Part of a yearly series' },
];



/** Payload keys in the words this form puts on their labels — a message must name what the user sees. */
const FIELD_WORDS: Record<string, string> = {
  title: 'the title', venue: 'the venue', latitude: 'the venue', longitude: 'the venue',
};
/** "the title and the venue" — a sentence, not a comma-separated field list. */
const joinWords = (parts: string[]): string =>
  parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/**
 * A read-only field, in the app's three-level hierarchy: the SECTION above is uppercase, the label
 * is sentence case and quiet, and the value outranks its own label. An uppercase label would compete
 * with its own heading — which is exactly how these views became hard to read before.
 */
function RField({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-0.5 type-body-strong text-content">
        {children ?? <span className="font-normal text-subtle">Not recorded</span>}
      </dd>
    </div>
  );
}

/** One counted fact in the identity band — what this record already is, beside what you are editing. */
function BandFact({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="text-right">
      <div className={cn('type-body-strong tabular-nums', tone)}>{value}</div>
      <div className="type-caption whitespace-nowrap text-subtle">{label}</div>
    </div>
  );
}

/**
 * WHAT YOU ARE EDITING, above the fields that edit it.
 *
 * The dialog used to open on eight grey boxes and a code in the title bar: nothing said whether the
 * meet had happened, who owns it, or that 21 people were already on its list — all of which change
 * what a given edit MEANS.
 */
function IdentityBand({ record, typeMeta, status, onList, notified, attended }: {
  record: any; typeMeta: { icon: ReactNode; chip: string; label: string }; status: string;
  onList: number; notified: number; attended: number;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-card border border-line bg-linear-to-r from-brand-500/[0.09] to-transparent px-3.5 py-3 shadow-e1">
      <span className={cn('grid size-10 shrink-0 place-items-center rounded-lg', typeMeta.chip)}>{typeMeta.icon}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <CopyValue value={record?.code} label="Event no">
            <span className="font-mono type-caption text-muted">{record?.code}</span>
          </CopyValue>
          <Tag tone={STATUS_TONE[status] ?? 'neutral'}>{statusLabel(status)}</Tag>
        </div>
        <div className="mt-0.5 type-caption text-subtle">
          {typeMeta.label}{record?.organiserName ? ` · organised by ${record.organiserName}` : ''}
        </div>
      </div>
      <div className="ml-auto flex items-start gap-4">
        <BandFact label="On the list" value={onList} tone="text-content" />
        <BandFact label="Invited" value={notified} tone={notified < onList ? 'text-warning-600 dark:text-warning-400' : 'text-content'} />
        {status !== 'PLANNED' && <BandFact label="Attended" value={attended} tone="text-success-600 dark:text-success-400" />}
      </div>
    </div>
  );
}

/** Create / edit an event (the core planning fields; the outcome is posted on the detail page). */
export function EventFormModal({ open, onClose, record }: { open: boolean; onClose: () => void; record?: any | null }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const isEdit = !!record;
  const status: string = record?.status ?? 'PLANNED';

  /**
   * WHETHER THE PLAN MAY STILL BE EDITED AT ALL.
   *
   * A meet that has happened is a historical record: the invitation named this title, this date and
   * this hall; attendance was marked against that list; the ratings and the outcome report are both
   * given against that agenda. Rewriting the plan afterwards silently re-labels all of it.
   *
   * The SERVER owns the rule (`planEditable`, from PLAN_EDITABLE in event.service.ts) and refuses the
   * write; this only decides whether to offer a form or the record. The `??` fallback spells the same
   * rule out, for a payload from a build released before the flag existed.
   */
  const planEditable = !isEdit || (record.planEditable ?? (status !== 'COMPLETED' && status !== 'CANCELLED'));

  /**
   * A FROZEN PLAN IS NOT A DEAD END — it is just a much smaller door.
   *
   * A misspelt hall or a typo in the name is wrong data about a real meet, and freezing it for ever
   * only pushes organisers into leaving events un-completed so they stay editable. So a Super Admin
   * may fix exactly the fields that describe the meet, and nothing that DECIDED anything: the date,
   * the type, the area, the expected attendance and the budget stay shut for everyone, because who
   * was invited, when it counted and what its turnout and cost were measured against cannot be
   * rewritten after the fact.
   *
   * The server owns the list (`planCorrectableFields`) and refuses the rest, so the dialog opens
   * exactly the fields the write will accept instead of a guess that drifts when that set changes.
   */
  const isSuperAdmin = useHasRole('SUPER_ADMIN');
  const canCorrect = isEdit && !planEditable && (record.planCorrectable ?? isSuperAdmin);
  const correctableFields: string[] = record?.planCorrectableFields ?? ['title', 'venue'];
  const correctable = (f: string) => canCorrect && correctableFields.includes(f);

  const { data: territories } = useQuery({
    queryKey: ['territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: open,
  });

  /**
   * A flat run of "PN-JUN · Junnar" next to "GJ-AHMEDABAD · Ahmedabad" is unreadable once the list is
   * national: every entry looks alike, and nothing tells you which state you are scrolling through.
   *
   * So the picker is GROUPED BY STATE, found by walking each territory up its `parentId` chain, and
   * ordered broadest-first inside each group (state, then its districts, then their beats) — which is
   * how someone actually narrows down. The searchable `label` still carries code, name and state, so
   * typing "junnar", "PN-JUN" or "maharashtra" all find it.
   */
  const territoryOptions = useMemo(() => {
    const all = territories ?? [];
    const byId = new Map(all.map((t: any) => [String(t.id), t]));
    const stateOf = (t: any): any => {
      let cur: any = t;
      const seen = new Set<string>();
      while (cur && cur.level !== 'state' && cur.parentId && !seen.has(String(cur.id))) {
        seen.add(String(cur.id));
        cur = byId.get(String(cur.parentId));
      }
      return cur?.level === 'state' ? cur : null;
    };
    const LEVEL_ORDER: Record<string, number> = { state: 0, zone: 1, district: 2, beat: 3 };
    const groups = new Map<string, { label: string; sort: string; options: any[] }>();
    for (const t of all) {
      const st = stateOf(t);
      const key = st ? String(st.id) : '__other';
      const label = st ? st.name : 'Other areas';
      if (!groups.has(key)) groups.set(key, { label, sort: label, options: [] });
      groups.get(key)!.options.push({
        value: String(t.id),
        name: t.name,
        code: t.code,
        level: t.level,
        stateName: st?.name ?? null,
        text: `${t.code} · ${t.name}`,
        label: [t.code, t.name, t.level, st?.name].filter(Boolean).join(' '),
      });
    }
    return [...groups.values()]
      .sort((a, b) => (a.label === 'Other areas' ? 1 : b.label === 'Other areas' ? -1 : a.sort.localeCompare(b.sort)))
      .map((g) => ({
        label: g.label,
        options: g.options.sort(
          (a, b) => (LEVEL_ORDER[a.level] ?? 9) - (LEVEL_ORDER[b.level] ?? 9) || String(a.name).localeCompare(String(b.name)),
        ),
      }));
  }, [territories]);

  /** The name, its code and the RUNG of the tree on ONE line, each in that level's own colour. */
  const renderTerritory = (o: any) => {
    const lvl = TERRITORY_LEVEL[String(o.level ?? '').toLowerCase()];
    return (
      <span className="flex min-w-0 items-center gap-2 py-0.5">
        <span className="min-w-0 flex-1 truncate font-medium text-content">{o.name}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="shrink-0 rounded border border-line bg-surface-2 px-1 py-px font-mono type-caption text-muted">{o.code}</span>
          {lvl ? <Tag tone={lvl.tone} icon={lvl.icon}>{lvl.label}</Tag> : <span className="type-caption capitalize text-subtle">{o.level}</span>}
        </span>
      </span>
    );
  };

  /** The chosen type, drawn the way the dropdown draws it, so the field and its options agree. */
  const renderType = (o: any) => (
    <span className="flex items-center gap-2">
      <span className={cn('grid size-6 shrink-0 place-items-center rounded-md', o.chip)}>{o.icon}</span>
      <span className="text-content">{o.label}</span>
    </span>
  );

  const renderCadence = (o: any) => (
    <span className="flex min-w-0 flex-col">
      <span className="text-content">{o.label}</span>
      <span className="type-caption text-subtle">{o.note}</span>
    </span>
  );

  /**
   * A new meet opens on TODAY (owner, 2026-09-28: "by default the start date should be current date"),
   * at the next half hour — but never before 10:00 AM, so a meet planned this morning does not open
   * already in the past. It is only a starting point, printed in the field, and changed in one click.
   */
  const defaultMeetStart = () => {
    const now = dayjs();
    const nextHalf = now.startOf('minute').add(30 - (now.minute() % 30), 'minute');
    const ten = now.startOf('day').add(10, 'hour');
    const pick = nextHalf.isBefore(ten) ? ten : nextHalf;
    return pick.isSame(now, 'day') ? pick : now.startOf('day').add(23 * 60 + 30, 'minute');
  };
  // Seed via initialValues + a per-target key (below), NOT setFieldsValue in an effect: the ui Form
  // seeds its store from initialValues on mount and the Radix Modal remounts the form each open, so an
  // open-effect seed raced the remount (blank fields) and let a prior event's values leak in (stale).
  const initialValues = useMemo(
    () =>
      record
        ? {
            title: record.title,
            type: record.type,
            cadence: record.cadence,
            territoryId: record.territoryId ? String(record.territoryId) : undefined,
            when: [record.startAt ? dayjs(record.startAt) : null, record.endAt ? dayjs(record.endAt) : null],
            venue: record.venue,
            capacity: record.capacity ?? undefined,
            budget: record.budget != null ? Number(record.budget) : undefined,
            description: record.description,
          }
        : { type: 'RETAILER_MEET', cadence: 'ONE_TIME', when: [defaultMeetStart(), null] },
    [record],
  );

  /*
   * WHAT THE NUMBERS ON THIS FORM MEAN TOGETHER.
   *
   * A budget of 18,000 against 40 expected people is 450 a head, and that is the figure anyone
   * approving it actually judges — but it was left as mental arithmetic between two bare boxes. Same
   * for the date: "07 Sep 2026, 10:00 AM" does not say which weekday it falls on, or that it is in
   * the past, and both of those are how you catch a date typed into the wrong month.
   */
  const watchedType = Form.useWatch('type', form) ?? initialValues.type;
  const watchedTerritory = Form.useWatch('territoryId', form);
  const watchedWhen = Form.useWatch('when', form);
  const watchedCapacity = Form.useWatch('capacity', form);
  const watchedBudget = Form.useWatch('budget', form);
  const typeMeta = TYPE_BY_VALUE.get(String(watchedType)) ?? TYPES[0];

  const start = watchedWhen?.[0] ? dayjs(watchedWhen[0]) : null;
  const startIsPast = !!start && start.isBefore(dayjs(), 'minute');
  // Calendar days, not 24-hour blocks: Wednesday 10 AM seen on Monday evening is "in 2 days", not "1".
  const daysAway = start ? start.startOf('day').diff(dayjs().startOf('day'), 'day') : 0;
  // The weekday and the length are already in the When field's own text ("Wed, 30 Sep 2026 · 10:00 AM
  // – 1:00 PM (3 h)"), so the line under it says only how far off it is — or that it has passed.
  const whenWords = start
    ? (startIsPast ? 'This date has already passed' : daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `In ${daysAway} days`)
    : null;
  const perHead = Number(watchedBudget) > 0 && Number(watchedCapacity) > 0
    ? Number(watchedBudget) / Number(watchedCapacity)
    : null;

  // What this record already IS — the facts the form edits around, and must not pretend away.
  const onList = Number(record?.assignedCount ?? record?.invitedCount ?? 0);
  const notified = Number(record?.notifiedCount ?? 0);
  const attended = Number(record?.attendedCount ?? 0);
  // Only a real MOVE — from one area to another. Some legacy events carry no area at all (the create
  // DTO did not require one until recently); filling that in adds a first list, not a second.
  const movedTerritory = isEdit && !!record?.territoryId && !!watchedTerritory
    && String(watchedTerritory) !== String(record.territoryId);
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const lockedOn = status === 'COMPLETED' ? record?.completedAt : record?.cancelledAt;

  const save = useConfirmedMutation(
    canCorrect
      ? {
          title: 'Correct this record?',
          content: 'This meet is already closed, so this changes how the record READS, not what the meet was. The change is saved under your name.',
          okText: 'Yes, correct it',
        }
      : { title: 'Save this event?', okText: 'Yes, save' },
    {
    mutationFn: (vals: any) => {
      // A correction sends only the fields a correction may touch. Posting the whole plan back would
      // be accepted (the server ignores what did not change) but it would put the frozen fields on
      // the wire, and the next person reading this would reasonably think they were editable.
      if (canCorrect) {
        return request({
          url: `/events/${record.id}`,
          method: 'PATCH',
          data: { title: vals.title, venue: vals.venue ?? '' },
        });
      }
      const [s, e] = vals.when ?? [];
      const payload: any = {
        title: vals.title,
        type: vals.type,
        cadence: vals.cadence,
        territoryId: vals.territoryId ?? undefined,
        venue: vals.venue || undefined,
        startAt: s ? dayjs(s).toISOString() : undefined,
        endAt: e ? dayjs(e).toISOString() : undefined,
        capacity: vals.capacity ?? undefined,
        budget: vals.budget ?? undefined,
        description: vals.description || undefined,
      };
      return isEdit
        ? request({ url: `/events/${record.id}`, method: 'PATCH', data: payload })
        : request({ url: '/events', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success(canCorrect ? 'Record corrected' : isEdit ? 'Event updated' : 'Event created');
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save event'),
  });

  const submit = async () => {
    const vals = await form.validateFields();
    save.mutate(vals);
  };

  /* The RECORD, not a form, once the meet is over — same five groups in the same order, so the plan
     reads where the reader expects to find it. */
  if (!planEditable) {
    const lvl = record?.territoryLevel ? TERRITORY_LEVEL[String(record.territoryLevel).toLowerCase()] : null;
    return (
      <Modal
        open={open}
        onOpenChange={(o) => { if (!o) onClose(); }}
        title={canCorrect ? 'Correct this record' : 'The plan for this meet'}
        width={680}
        autoFocus={!canCorrect ? false : undefined}
        footer={canCorrect
          ? <>
              <Button variant="default" onClick={onClose}>Cancel</Button>
              <Button variant="primary" loading={save.isPending} onClick={submit}>Save correction</Button>
            </>
          : <Button variant="primary" onClick={onClose}>Close</Button>}
      >
        <IdentityBand record={record} typeMeta={typeMeta} status={status} onList={onList} notified={notified} attended={attended} />
        <Alert
          type={canCorrect ? 'warning' : status === 'COMPLETED' ? 'info' : 'warning'}
          showIcon
          icon={<Lock size={16} />}
          message={canCorrect
            ? 'You are correcting a closed record, not planning a meet'
            : status === 'COMPLETED' ? 'This meet is over, so its plan is locked' : 'This meet was cancelled, so its plan is locked'}
          /* What is open, what is shut, and WHY the shut half is shut — a lock that gives no reason
             reads as a bug, and the person hitting it goes looking for a way round. */
          description={canCorrect
            ? `Only ${joinWords(correctableFields.map((f) => FIELD_WORDS[f] ?? f).filter((w, i, a) => a.indexOf(w) === i))} can be fixed here, because they describe the meet without deciding anything about it. The date, the type, the territory, the expected attendance and the budget stay shut for everyone: they set who was invited, when it counted, and what the turnout and cost were measured against. If one of those is wrong, cancel this record and enter the meet that really happened.`
            : status === 'COMPLETED'
              ? `Everyone was invited to this title, on this date, at this venue${lockedOn ? `, and it was closed on ${formatIst(lockedOn)}` : ''}. Changing any of it now would re-label the attendance, the ratings and the report that were all recorded against it. The outcome, the decisions and the photos are still yours to write — open the event and use the Outcome & Photos tab. A Super Admin can correct the title or the venue.`
              : 'A meet that was called off keeps the plan it was called off from. Plan the replacement as a new event.'}
          className="mb-4"
        />

        <Form key={record?.id ?? 'locked'} form={form} layout="vertical" initialValues={initialValues} className="space-y-5">
          <section>
            <SectionHead icon={<Users size={13} />} tint={SECTION_TINT.identity}>The meet</SectionHead>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {correctable('title') ? (
                <Form.Item name="title" label="Title" className="mb-0" rules={[{ required: true, message: 'Enter a title' }]}>
                  <Input autoFocus placeholder="e.g. Kharif farmer meet — Pune East" />
                </Form.Item>
              ) : <RField label="Title">{record?.title}</RField>}
              <RField label="Type">
                <span className="flex items-center gap-2">
                  <span className={cn('grid size-6 shrink-0 place-items-center rounded-md', typeMeta.chip)}>{typeMeta.icon}</span>
                  {typeMeta.label}
                </span>
              </RField>
              <RField label="Cadence">{(CADENCES.find((c) => c.value === record?.cadence) ?? CADENCES[0]).label}</RField>
              <RField label="Organiser">{record?.organiserName}</RField>
            </dl>
          </section>

          <section>
            <SectionHead icon={<MapPin size={13} />} tint={SECTION_TINT.place}>Where</SectionHead>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <RField label="Territory">
                <span className="flex flex-wrap items-center gap-2">
                  {record?.territoryName ?? null}
                  {lvl && <Tag tone={lvl.tone} icon={lvl.icon}>{lvl.label}</Tag>}
                </span>
              </RField>
              {correctable('venue') ? (
                <Form.Item name="venue" label="Venue" className="mb-0">
                  <Input placeholder="e.g. Gram Panchayat Hall, Junnar" prefix={<MapPin size={14} className="text-subtle" />} />
                </Form.Item>
              ) : <RField label="Venue">{record?.venue}</RField>}
            </dl>
          </section>

          <section>
            <SectionHead icon={<CalendarDays size={13} />} tint={SECTION_TINT.assignment}>When</SectionHead>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <RField label="Started">{record?.startAt ? formatIst(record.startAt) : undefined}</RField>
              <RField label="Ended">{record?.endAt ? formatIst(record.endAt) : undefined}</RField>
            </dl>
          </section>

          <section>
            <SectionHead icon={<Wallet size={13} />} tint={SECTION_TINT.money}>Scale &amp; budget</SectionHead>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <RField label="Expected attendees">{record?.capacity ? `${record.capacity} planned` : undefined}</RField>
              <RField label="Budget">{Number(record?.budget) > 0 ? inr(record.budget) : undefined}</RField>
            </dl>
          </section>

          <section>
            <SectionHead icon={<FileText size={13} />} tint={SECTION_TINT.documents}>Agenda / about</SectionHead>
            <p className="whitespace-pre-wrap type-body leading-relaxed text-content">
              {record?.description || <span className="text-subtle">No agenda was written for this meet.</span>}
            </p>
          </section>
        </Form>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? 'Edit the plan' : 'Plan a meet'}
      description={isEdit ? undefined : 'Everyone the territory covers — its retailers and its farmers — goes onto the invitation list.'}
      width={680}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={submit}>{isEdit ? 'Save changes' : 'Create event'}</Button>
      </>}
    >
      {isEdit && (
        <IdentityBand record={record} typeMeta={typeMeta} status={status} onList={onList} notified={notified} attended={attended} />
      )}

      <Form key={record?.id ?? 'new'} form={form} layout="vertical" initialValues={initialValues}>
        <section className="mb-5">
          <SectionHead icon={<Users size={13} />} tint={SECTION_TINT.identity}>The meet</SectionHead>
          <Form.Item
            name="title" label="Title" rules={[{ required: true, message: 'Enter a title' }]}
            extra="What the invitation will call it — name the season or the subject, not just the type."
          >
            <Input placeholder="e.g. Kharif farmer meet — Pune East" />
          </Form.Item>
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Pick what kind of meet this is' }]}>
              <Select options={TYPE_OPTIONS} optionRender={renderType} optionLabelProp="selectedText" />
            </Form.Item>
            <Form.Item
              name="cadence" label="Cadence" rules={[{ required: true, message: 'Pick how often it runs' }]}
              extra="A label for reporting — nothing is scheduled automatically."
            >
              <Select options={CADENCES} optionRender={renderCadence} optionLabelProp="label" />
            </Form.Item>
          </div>
        </section>

        <section className="mb-5">
          <SectionHead icon={<MapPin size={13} />} tint={SECTION_TINT.place}>Where</SectionHead>
          <Form.Item
            name="territoryId" label="Territory" rules={[{ required: true, message: 'Pick the territory this meet is for' }]}
            /* What picking it DOES, stated in terms of THIS record: a create invites the area, an edit
               that leaves it alone changes nothing, and only a move adds people. The old copy promised
               the first of those on all three. */
            extra={!isEdit || movedTerritory
              ? 'You can only place a meet in a territory you cover.'
              : !record?.territoryId
                ? 'This meet has no territory, so nobody was ever invited from one. Pick it and that territory’s retailers & farmers go on the list.'
                : onList
                  ? `${plural(onList, 'retailer & farmer', 'retailers & farmers')} of this territory ${onList === 1 ? 'is' : 'are'} on the list.`
                  : 'Nobody is on the list yet — add them from the Participants tab.'}
          >
            <Select
              showSearch optionFilterProp="label" optionLabelProp="text" optionRender={renderTerritory}
              placeholder="Search by territory, code or state" options={territoryOptions}
            />
          </Form.Item>
          {movedTerritory && (
            <Alert
              type="warning" showIcon className="mb-4"
              message="Moving the meet adds a second territory's people"
              description={`Every retailer and farmer of the new territory goes on the list when you save. The ${onList} already on it stay${notified ? `, and ${notified} of them have already been told the old details` : ''}. Take off anyone who should not come from the Participants tab.`}
            />
          )}
          <Form.Item name="venue" label="Venue" extra="The hall or address people should turn up to.">
            <Input placeholder="e.g. Gram Panchayat Hall, Junnar" prefix={<MapPin size={14} className="text-subtle" />} />
          </Form.Item>
        </section>

        <section className="mb-5">
          <SectionHead icon={<CalendarDays size={13} />} tint={SECTION_TINT.assignment}>When</SectionHead>
          <Form.Item
            name="when" label="Date & time"
            rules={[
              { required: true, message: 'Pick the date & time' },
              { validator: async (_r: any, v: any) => { if (v?.[0] && v?.[1] && !dayjs(v[1]).isAfter(dayjs(v[0]))) throw new Error('The meet must end after it starts'); } },
            ]}
            /* The weekday and how far off it is: the two things nobody can read out of a date string,
               and the two that catch a date typed into the wrong month. */
            extra={whenWords
              ? <span className={startIsPast && status === 'PLANNED' ? 'text-warning-600 dark:text-warning-400' : undefined}>{whenWords}</span>
              : undefined}
          >
            <MeetWhenField />
          </Form.Item>
        </section>

        <section className="mb-5">
          <SectionHead icon={<Wallet size={13} />} tint={SECTION_TINT.money}>Scale &amp; budget</SectionHead>
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <Form.Item
              name="capacity" label="Expected attendees"
              /* This number is not only a plan: it is the seat limit self-registration counts against,
                 and a form that hides that lets someone cap a meet by accident. */
              extra="Also the seat limit — customers stop being able to confirm a seat once this many have."
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 50" addonAfter="people" />
            </Form.Item>
            <Form.Item
              name="budget" label="Budget"
              extra={perHead != null ? `${inr(perHead)} per expected attendee` : 'Hall, refreshments, travel — what this meet is allowed to cost.'}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="optional" prefix="₹" />
            </Form.Item>
          </div>
        </section>

        <section>
          <SectionHead icon={<FileText size={13} />} tint={SECTION_TINT.documents}>Agenda / about</SectionHead>
          {/* Required: attendees are told what they are coming to, and the post-event report measures
              the outcome against it — a report with no plan can only describe what happened. */}
          <Form.Item
            name="description" label="What this meet is for"
            rules={[{ required: true, message: 'Write what this meet is for — it drives the invitation and the post-event report' }]}
            extra="Goes out with the invitation, and the outcome report is written against it."
          >
            <Input.TextArea rows={3} placeholder="Planned agenda, topics, demos…" />
          </Form.Item>
        </section>
      </Form>
    </Modal>
  );
}
